import React, { useEffect } from 'react';

import {
  EVENT_OPTIONS, AGE_OPTIONS, DESCRIPTOR_TYPE_OPTIONS, GENDER_OPTIONS_STRICT, DEEPFAKE_STATE_OPTIONS_STRICT,
} from '@vlabs/api-bindings/src/constants';
import { LIVENESS_OPTIONS_STRICT } from '@vlabs/api-bindings/src/luna-client/constants';
import SearchByEvents from '@vlabs/shared/legacy-components/search-by/SearchByEvents';
import SearchByFaces from '@vlabs/shared/legacy-components/search-by/SearchByFaces';
import { selectIsAppFeatureAvailable } from '@vlabs/shared/selectors/appSelectors';
import validate from '@vlabs/shared/validators';
import {
  Page, Control, Divider, Grid, GridRow, GridCol,
} from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import st from './MatchPolicyForm.module.sass';

function MatchPolicyForm({
  onSubmit,
  isSelectedMatch = false,
  handlerOptions = [],
  listOptions = [],
  extract = {},
  matchPolicyList = [],
  initialValues = {},
  isBodyLicenseAvailable = false,
}) {
  const { t } = useTranslation();

  const isFaceDescriptorExtract = useWatch({ name: 'extract.faceDescriptor' });
  const isBodyDescriptorExtract = useWatch({ name: 'extract.bodyDescriptor' });
  const livenessIsEnabled = useWatch({ name: 'estimate_liveness.estimate' });
  const deepfakeIsEnabled = useWatch({ name: 'estimate_deepfake.estimate' });

  const formMethods = useForm({
    defaultValues: {
      ...initialValues,
      origin: initialValues.origin || EVENT_OPTIONS.faces,
      descriptorType: initialValues.descriptorType || DESCRIPTOR_TYPE_OPTIONS.face,
    },
  });

  const validateMatchPolicyLabel = (value) => {
    const isLabelExist = matchPolicyList?.some(
      // в matchPolicyList[i].label может лежать просто название label или listId
      ({ label }) => label === value && label !== initialValues.label,
    );

    if (!isLabelExist) {
      return true;
    }
    return t('comparison_parameter_name_exists');
  };
  const {
    watch,
    control,
    handleSubmit: originalHandlerSubmit,
    register,
    setValue,
    setError,
    formState: {
      errors,
    },
  } = formMethods;

  const faceSearchType = watch('origin')?.value === 'faces';
  const eventSearchType = watch('origin')?.value === 'events';
  const bodyDescriptorType = watch('descriptorType').value === 'body';
  const faceDescriptorType = watch('descriptorType').value === 'face';
  const selectedList = watch('list');
  const selectedListId = selectedList?.value;
  const shouldUseListIdAsLabel = faceSearchType && Boolean(selectedListId);

  // Предотвращает сабмит основной формы
  const handleSubmit = (func) => (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    originalHandlerSubmit(func)(e);
  };

  const $onSubmit = (values) => {
    const label = shouldUseListIdAsLabel ? selectedListId : values.label;
    const labelValidationResult = validateMatchPolicyLabel(label);

    if (labelValidationResult !== true) {
      setError('label', {
        type: 'validate',
        message: labelValidationResult,
      });
      return;
    }

    onSubmit({ ...values, label });
  };

  const selectedListLabelError = shouldUseListIdAsLabel
    ? validateMatchPolicyLabel(selectedListId)
    : true;

  useEffect(() => {
    const sub = watch((data, { name }) => {
      if (name === 'origin' && data.origin.value === 'faces') {
        setValue('descriptorType', DESCRIPTOR_TYPE_OPTIONS.face);
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    // автозаполняем label только для поиска по списку, чтобы не затирать пользовательское название
    if (!shouldUseListIdAsLabel) return;

    // тут вручную устанавливаем значение label при изменении СПИСКА если выбрано "Идентифицировать среди - Лиц"
    // если список выбран из уже созданых, то устанавилваем selectedList.label, а если создали вручную то выбираем value
    // во втором случае в value будет listId
    setValue('label', selectedList?.label || selectedList?.value || '');
  }, [shouldUseListIdAsLabel, selectedList?.label, selectedList?.value, setValue]);

  const context = {
    handlerOptions, listOptions, isBodyLicenseAvailable, ...formMethods,
  };

  const commonFilters = (
    <React.Fragment>
      <div>
        <Divider small />
        <div className={st.Subtitle}>
          {t('handlers:filter_search_result_by')}
        </div>
        <Divider small />
        <div className={st.BasicAttributesFilter}>
          <div className={st.Width_2}>
            <Control.Select
              control={control}
              disabled={!extract?.basicAttributes}
              isClearable
              name="filters.gender"
              options={GENDER_OPTIONS_STRICT.raw}
              placeholder={t('gender')}
            />
          </div>
          <div className={st.Width_2}>
            <Control.Select
              control={control}
              disabled={!extract?.basicAttributes}
              isClearable
              name="filters.age"
              options={AGE_OPTIONS.raw}
              placeholder={t('age')}
            />
          </div>

          <div className={st.Width_2}>
            <Control.Select
              control={control}
              disabled={!livenessIsEnabled}
              isMulti
              name="filters.liveness"
              options={LIVENESS_OPTIONS_STRICT.raw}
              placeholder={t('Liveness')}
            />
          </div>
          <div className={st.Width_2}>
            <Control.Select
              control={control}
              disabled={!deepfakeIsEnabled}
              isMulti
              name="filters.deepfake"
              options={DEEPFAKE_STATE_OPTIONS_STRICT.raw}
              placeholder={t('Deepfake')}
            />
          </div>
        </div>
      </div>
      <Divider small />
      <div>
        <div className={st.Subtitle}>
          {t('handlers:additional_search_parameters')}
        </div>
        <Divider small />
        <Grid>
          <GridRow>
            <GridCol>
              <Control.Input
                label={t('handlers:max_similar_in_search_results')}
                {...register('limit', {
                  min: validate.gte(1),
                  max: validate.lte(100),
                  validate: {
                    isInteger: (v) => Number.isInteger(Number(v)) || t('validation.number.integer'),
                  },
                })}
                errors={errors}
                placeholder={t('not_specified')}
              />
            </GridCol>
            <GridCol>
              <Control.Input
                label={t('handlers:accuracy_threshold')}
                {...register('threshold', {
                  min: validate.gte(0),
                  max: validate.lte(1),
                  validate: validate.number(),
                })}
                errors={errors}
                placeholder={t('not_specified')}
              />
            </GridCol>
          </GridRow>
        </Grid>
      </div>
    </React.Fragment>
  );

  return (
    <FormProvider {...context}>
      <Page
        className={st.Page}
        title={isSelectedMatch
          ? t('handlers:edit_label')
          : t('handlers:add_label')}
      >
        <form onSubmit={handleSubmit($onSubmit)}>
          <div className={cn(st.BlocksWrapper, st.BlocksWrapper_flex)}>
            <div className={st.Subtitle}>
              {t('handlers:policies.match_policy.label')}
            </div>
            <div className={st.Width_1}>
              {shouldUseListIdAsLabel ? (
                <Control.Input
                  disabled
                  errorMessage={selectedListLabelError ?? undefined}
                  value={selectedList?.label || selectedList?.value || ''}
                />
              ) : (
                <Control.Input
                  errors={errors}
                  {...register('label', {
                    required: validate.required(),
                    validate: validateMatchPolicyLabel,
                    maxLength: validate.maxLength(36),
                  })}
                />
              )}
            </div>
          </div>

          <div className={cn(st.BlocksWrapper, st.BlocksWrapper_flex)}>
            <div className={st.Subtitle}>
              {t('handlers:identify_among')}
            </div>
            <div className={st.Width_1}>
              <Control.Select
                control={control}
                name="origin"
                options={EVENT_OPTIONS.raw}
              />
            </div>
          </div>
          <div className={cn(st.BlocksWrapper, st.BlocksWrapper_flex)}>
            <div className={st.Subtitle}>
              {t('handlers:policies.match_policy.search for biometric template')}
            </div>
            <div className={st.Width_1}>
              <Control.Select
                control={control}
                disabled={faceSearchType}
                name="descriptorType"
                options={DESCRIPTOR_TYPE_OPTIONS.raw}
              />
            </div>
          </div>

          <div className={st.BlocksWrapper}>
            <div className={st.Subtitle}>
              {t('handlers:perform_search_by')}
            </div>
            <Divider small />
            {!!isFaceDescriptorExtract && !!faceSearchType && (
              <React.Fragment>
                <SearchByFaces />
                {commonFilters}
              </React.Fragment>
            )}
            {!!eventSearchType && !!isFaceDescriptorExtract && !!faceDescriptorType && (
              <React.Fragment>
                <SearchByEvents />
                {commonFilters}
              </React.Fragment>
            )}

            {!!eventSearchType && !!isBodyDescriptorExtract && !!bodyDescriptorType && (
              <React.Fragment>
                <SearchByEvents />
                {commonFilters}
              </React.Fragment>
            )}

            {!isFaceDescriptorExtract && !!faceDescriptorType && <div>{t('handlers:policies.match.enable biometric face template extraction')}</div>}
            {!isBodyDescriptorExtract && !!bodyDescriptorType && <div>{t('handlers:policies.match.enable biometric body template extraction')}</div>}
          </div>

          <Divider small />
          <Control.Button
            disabled={
              (!isBodyDescriptorExtract && bodyDescriptorType)
              || (!isFaceDescriptorExtract && faceDescriptorType)
            }
            fullWidth
            type="submit"
          >
            {isSelectedMatch ? t('btn.change') : t('btn.add')}
          </Control.Button>
        </form>
      </Page>
    </FormProvider>
  );
}

const optionPropType = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.any,
});

MatchPolicyForm.propTypes = {
  isBodyLicenseAvailable: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  isSelectedMatch: PropTypes.bool,
  handlerOptions: PropTypes.arrayOf(optionPropType),
  listOptions: PropTypes.arrayOf(optionPropType),
  extract: PropTypes.shape({
    basicAttributes: PropTypes.bool,
  }),
  matchPolicyList: PropTypes.arrayOf(optionPropType),
  initialValues: PropTypes.shape({
    label: PropTypes.string,
    origin: optionPropType,
    descriptorType: optionPropType,
  }),
};

export default connect((state) => ({
  isBodyLicenseAvailable: selectIsAppFeatureAvailable(state, 'estimations.body_attributes'),
}))(MatchPolicyForm);
